import { css } from "@emotion/css";
import type { CalloutType } from "@octopusdeploy/design-system-components";
import { Tooltip, Callout } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { ArgoCDGatewayResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { exhaustiveCheck } from "@octopusdeploy/type-utils";
import type React from "react";
import { useCallback, useMemo } from "react";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import DateFormatter from "~/utils/DateFormatter/index";
const styles = {
    link: css({
        cursor: "pointer",
    }),
    description: css({
        marginBottom: space[12],
    }),
};
type ArgoCDGatewayHealthCalloutProps = {
    gateway: ArgoCDGatewayResource;
};
export const ArgoCDGatewayHealthCallout = (props: ArgoCDGatewayHealthCalloutProps) => {
    const { gateway } = props;
    const { navigate } = useSpaceAwareNavigation();
    const createServerTaskLink = useCallback((label: string, taskId: string) => {
        const serverTaskLink = links.taskPage.generateUrl({ taskId });
        return (<Tooltip content={"View task"}>
                    <a className={styles.link} onClick={() => navigate(serverTaskLink)}>
                        {label}
                    </a>
                </Tooltip>);
    }, [navigate]);
    const [type, title, description]: [
        CalloutType,
        string,
        string | React.ReactElement
    ] = useMemo(() => {
        const serverTaskSuffix = gateway.HealthCheckServerTaskId && gateway.HealthLastChecked ? <span> Last health check was {createServerTaskLink(DateFormatter.momentAgo(gateway.HealthLastChecked), gateway.HealthCheckServerTaskId)}.</span> : undefined;
        switch (gateway.HealthStatus) {
            case "Unknown":
                return ["information", "Unknown", "The gateway was just added, and a health check has not been performed."];
            case "Healthy":
                return ["success", "Healthy", <span>The gateway has established a connection with Argo CD.{serverTaskSuffix}</span>];
            case "ArgoCDConnectionIssues":
                return ["warning", "Argo CD Connectivity Issues", <span>The gateway is unable to establish a connection with the Argo CD instance.{serverTaskSuffix}</span>];
            case "Unavailable":
                return ["danger", "Unavailable", <span>Unable to establish a connection with the Argo CD gateway.{serverTaskSuffix}</span>];
            default:
                exhaustiveCheck(gateway.HealthStatus, "Unknown health status type");
        }
    }, [createServerTaskLink, gateway.HealthCheckServerTaskId, gateway.HealthLastChecked, gateway.HealthStatus]);
    return (<Callout type={type} title={title}>
            <div className={styles.description}>{description}</div>
        </Callout>);
};
